import React, { useState } from "react";
import AsyncDataTable from "@/components/core/async-data-table/async-data-table";
import {
  DatableColumnType,
  FilterType,
  propertyType,
} from "@/components/core/async-data-table/type";
import { endpoints } from "@/api/endpoints/endpoints";
import FilterHeader from "@/components/core/async-data-table/filter-header";
import ActionPopover from "@/components/action/action-popover";
import { Plus } from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useDeleteFulfillment } from "@/services/fulfillment-service";
import {
  FulfillmentMethod,
  FulfillmentCarrier,
} from "@/types/fulfillment-type";

const FulfillmentAsyncTable = () => {
  const [, setLocation] = useLocation();
  const deleteFulfillment = useDeleteFulfillment();
  const [refreshPage, setRefreshPage] = useState(false);
  const { toast } = useToast();

  const handleDelete = async (fulfillmentId: string) => {
    try {
      await deleteFulfillment.execute(fulfillmentId);
      toast({
        title: "Success",
        description: "Fulfillment deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete fulfillment",
        variant: "destructive",
      });
    }

    setRefreshPage(!refreshPage);
  };

  const cols: DatableColumnType[] = [
    {
      name: "orderId",
      id: "orderId",
      label: "Order ID",
      searchable: true,
      orderable: true,
      datatype: "string",
    },
    {
      name: "method",
      id: "method",
      label: "Method",
      searchable: true,
      orderable: true,
      datatype: "custom",
      Render: ({ row }: { row: any }) => {
        if (!row.method)
          return <span className="text-sm text-gray-400">-</span>;
        const methodLabels: Record<FulfillmentMethod, string> = {
          [FulfillmentMethod.PICKUP]: "Pickup",
          [FulfillmentMethod.DELIVERY_CITY]: "Delivery City",
          [FulfillmentMethod.DELIVERY_PROVINCE]: "Delivery Province",
          [FulfillmentMethod.UNKNOWN]: "Unknown",
        };
        return (
          <span className="text-sm text-gray-700 font-medium">
            {methodLabels[row.method as FulfillmentMethod] || row.method}
          </span>
        );
      },
    },
    {
      name: "carrier",
      id: "carrier",
      label: "Carrier",
      searchable: true,
      orderable: true,
      datatype: "custom",
      Render: ({ row }: { row: any }) => {
        if (!row.carrier)
          return <span className="text-sm text-gray-400">-</span>;
        const carrierLabels: Record<FulfillmentCarrier, string> = {
          [FulfillmentCarrier.VIREAK_BUNTHAM]: "Vireak Buntham",
          [FulfillmentCarrier.JNT]: "J&T",
        };
        return (
          <span className="text-sm text-gray-700 font-medium">
            {carrierLabels[row.carrier as FulfillmentCarrier] || row.carrier}
          </span>
        );
      },
    },
    {
      name: "price",
      id: "price",
      label: "Price",
      searchable: true,
      orderable: true,
      datatype: "custom",
      Render: ({ row }: { row: any }) => {
        if (!row.price) return <span className="text-sm text-gray-400">-</span>;
        const price = parseFloat(row.price);
        return (
          <span className="text-sm text-gray-700 font-medium">
            $
            {price.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      },
    },
    {
      name: "action",
      id: "action",
      label: "Action",
      Render: ({ row }: { row: any }) => (
        <ActionPopover
          onEdit={() => {
            setLocation(`/fulfillments/${row.id}/edit`);
          }}
          onDelete={() => {
            handleDelete(row.id);
          }}
        />
      ),
    },
  ];

  const defaultFilterProp: propertyType[] = [
    { propName: "orderId", value: "", type: FilterType.STRING },
    { propName: "method", value: "", type: FilterType.STRING },
    { propName: "carrier", value: "", type: FilterType.STRING },
    { propName: "price", value: "", type: FilterType.STRING },
  ];

  return (
    <AsyncDataTable
      dataUrl={endpoints.fulfillment.list}
      columns={cols}
      autoIndex
      FilterComponent={FilterHeader}
      refreshPage={refreshPage}
      defaultFilterProp={defaultFilterProp}
      showPageHeader={false}
      pageTitle="Fulfillments"
      pageSubtitle="Manage your fulfillments"
      headerAction={() => { }}
      headerActionText="Create Fulfillment"
      headerActionIcon={<Plus size={18} />}
    />
  );
};

export default FulfillmentAsyncTable;
